// Resume import — extract text from a PDF/TXT (or LinkedIn "Save to PDF" export)
// or pasted text, then parse it into the builder's structure. Heuristic (no API
// cost); the editor + Premium AI polish the result.

const uid = () => Math.random().toString(36).slice(2, 9);

// ── PDF → text (pdf.js, lazy-loaded so it never bloats the main bundle) ──────────
// Reconstructs real lines + word spacing from positioned text runs. pdf.js returns
// runs without reliable spaces, so we insert a space on an X-gap and break a line
// on hasEOL or a downward Y jump.
export async function extractPdfText(file) {
  const mod = await import("pdfjs-dist/legacy/build/pdf");
  const pdfjs = mod && mod.getDocument ? mod : (mod.default || mod);
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pages = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const tc = await page.getTextContent();
    const lines = [];
    let line = "", endX = null, lastY = null;
    const flush = () => { if (line.replace(/\s/g, "")) lines.push(line.replace(/\s+$/, "")); line = ""; endX = null; };
    for (const it of tc.items) {
      const str = it.str || "";
      const x = it.transform[4], y = it.transform[5];
      const fs = Math.abs(it.transform[0]) || it.height || 6;
      if (lastY !== null && line && (lastY - y) > fs * 0.6) flush();   // new visual line
      if (line && endX !== null) {
        const gap = x - endX;
        if (gap > fs * 0.25 && !/\s$/.test(line) && !/^\s/.test(str)) line += " ";
      }
      line += str;
      endX = x + (it.width || 0);
      lastY = y;
      if (it.hasEOL) flush();
    }
    flush();
    pages.push(lines.join("\n"));
  }
  try { await pdf.destroy(); } catch {}
  return pages.join("\n");
}

export function readFileText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsText(file);
  });
}

// ── heuristic parser ─────────────────────────────────────────────────────────────
// Exact-match header phrases (precise — avoids mistaking a job title like
// "Experience Designer" for an "Experience" section header).
const HEADERS = {
  summary: ["summary", "professional summary", "career summary", "executive summary", "profile", "professional profile", "objective", "career objective", "about", "about me", "overview"],
  experience: ["experience", "work experience", "professional experience", "relevant experience", "employment", "employment history", "work history", "career history", "professional background", "professional experience & achievements"],
  education: ["education", "academic background", "academics", "education & training", "education and training", "academic qualifications"],
  skills: ["skills", "technical skills", "core skills", "key skills", "core competencies", "competencies", "areas of expertise", "expertise", "technical proficiencies", "proficiencies", "technologies", "skills & abilities", "skills and abilities", "tools & technologies"],
  projects: ["projects", "key projects", "selected projects", "personal projects", "portfolio", "notable projects"],
  certifications: ["certifications", "certification", "certificates", "licenses", "licences", "certifications & licenses", "licenses & certifications", "accreditations", "awards & certifications", "courses & certifications"],
  languages: ["languages", "language"],
};
const MONTH = "(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\\.?";
const DATE_TOKEN = `(?:${MONTH}\\s*)?(?:19|20)\\d{2}`;
const DATE_RANGE = new RegExp(`(${DATE_TOKEN})\\s*(?:[-–—]|to|until|\\u2013)\\s*(${DATE_TOKEN}|present|current|now|ongoing|date)`, "i");
// Require a 2-letter state/region OR a known country so a "Title, Company" line
// isn't mistaken for "City, State".
const LOCATION = /([A-Z][a-zA-Z.\-']+(?:\s+[A-Z][a-zA-Z.\-']+){0,2}),\s*([A-Z]{2}\b|England|Scotland|Wales|Ireland|Canada|Australia|Germany|France|Spain|Italy|India|Brazil|Mexico|Netherlands|Singapore|Remote|Hybrid)\b/;
const ACTION_VERB = /^(led|built|managed|developed|designed|created|improved|increased|launched|implemented|drove|owned|delivered|reduced|spearheaded|coordinated|analyzed|maintained|collaborated|oversaw|established|streamlined|optimized|achieved|generated|negotiated|supervised|trained|wrote|researched|conducted|handled|provided|ensured|supported|worked|responsible|assisted|performed)\b/i;
const sentenceLike = (l) => l.length > 45 || /[.!]$/.test(l) || ACTION_VERB.test(l);
const stripTrail = (s) => s.replace(/[\s,;:|·•–—\-]+$/, "").trim();
const stripLead = (s) => s.replace(/^[\s,;:|·•–—\-]+/, "").trim();

function normHeader(line) {
  return line.trim().toLowerCase().replace(/[:.\-–—|•·]+$/g, "").replace(/\s+/g, " ").trim();
}
function headerKey(line) {
  const t = line.trim();
  if (!t || t.length > 34 || /[.!?]$/.test(t)) return null;
  const n = normHeader(line);
  for (const k of Object.keys(HEADERS)) if (HEADERS[k].includes(n)) return k;
  return null;
}
const isBullet = (l) => /^\s*[•▪◦‣·*\-–—o]\s+/.test(l) || /^\s*[•▪◦‣]/.test(l);
const cleanBullet = (l) => l.replace(/^\s*[•▪◦‣·*\-–—o]+\s*/, "").trim();
function isDateOnly(l) {
  if (!DATE_RANGE.test(l) && !/\b(19|20)\d{2}\b/.test(l)) return false;
  const rest = l.replace(DATE_RANGE, "").replace(/\b(19|20)\d{2}\b/g, "")
    .replace(new RegExp(MONTH, "ig"), "").replace(/present|current|now|ongoing|date/ig, "")
    .replace(/[\s\-–—|,•·/]/g, "");
  return rest.length <= 3;
}

function toBlocks(lines) {
  const out = []; let cur = [];
  for (const l of lines) { if (!l.trim()) { if (cur.length) { out.push(cur); cur = []; } } else cur.push(l); }
  if (cur.length) out.push(cur);
  return out;
}

function buildExperience(header, bulletArr) {
  let start = "", end = "", location = "";
  for (const l of header) { const m = l.match(DATE_RANGE); if (m) { start = m[1].trim(); end = /present|current|now|ongoing|date/i.test(m[2]) ? "" : m[2].trim(); break; } }
  if (!start) for (const l of header) { const y = l.match(/\b(19|20)\d{2}\b/); if (y && isDateOnly(l)) { start = y[0]; break; } }
  for (const l of header) { const loc = l.match(LOCATION); if (loc) { location = loc[0]; break; } }

  const deDate = (s) => s.replace(DATE_RANGE, "").replace(/\b(19|20)\d{2}\b/g, "").replace(/\(\s*\)/g, "").replace(/\[\s*\]/g, "");
  let first = stripTrail(stripLead(deDate(header[0] || "")));
  if (location) first = stripTrail(first.replace(location, ""));
  let title = first, company = "";
  const parts = first.split(/\s+(?:at|@)\s+|\s+[—–|·]\s+|\s+-\s+|,\s+/i)
    .map(s => stripTrail(stripLead(s))).filter(p => p && !/^[()\[\].\-–—|·]+$/.test(p));
  if (parts.length >= 2 && parts[0].length <= 70) { title = parts[0]; company = parts.slice(1).join(", "); }
  else if (parts.length === 1) title = parts[0];
  // company often sits on the 2nd header line
  if (!company) {
    for (let i = 1; i < header.length; i++) {
      if (isDateOnly(header[i])) continue;
      let l2 = stripTrail(stripLead(deDate(header[i])));
      if (location) l2 = stripTrail(l2.replace(location, ""));
      if (l2 && l2.length <= 80) { company = l2.split(/\s+[—–|·]\s+|,\s/)[0].trim(); break; }
    }
  }
  const bullets = bulletArr.join("\n").trim();
  if (!title && !company && !bullets) return null;
  return { id: uid(), title, company, location, start, end, bullets };
}

function parseExperience(lines) {
  const entries = [];
  let cur = null, lastWasDate = false;
  const pushCur = () => { if (cur) { const e = buildExperience(cur.header, cur.bullets); if (e) entries.push(e); } };
  for (const raw of lines) {
    const l = raw.replace(/\s+$/, "");
    if (!l.trim()) { lastWasDate = false; continue; }
    if (isBullet(l)) { if (!cur) cur = { header: [], bullets: [] }; cur.bullets.push(cleanBullet(l)); lastWasDate = false; continue; }
    if (isDateOnly(l)) { if (!cur) cur = { header: [], bullets: [] }; cur.header.push(l.trim()); lastWasDate = true; continue; }
    // A descriptive sentence (no bullet marker) belongs to the current role, not a
    // new entry — otherwise unbulleted descriptions get split into fake jobs.
    if (cur && sentenceLike(l.trim())) { cur.bullets.push(l.trim()); lastWasDate = false; continue; }
    // A short, header-like line starts a new entry after bullets / a date / at start.
    if (!cur || cur.bullets.length || lastWasDate) { pushCur(); cur = { header: [l.trim()], bullets: [] }; }
    else cur.header.push(l.trim());
    lastWasDate = false;
  }
  pushCur();
  return entries.slice(0, 15);
}

function parseEducation(lines) {
  // split into blocks; if a block packs several year-bearing lines, treat each as one
  const raw = [];
  for (const b of toBlocks(lines)) {
    const yearLines = b.filter(l => /\b(19|20)\d{2}\b/.test(l));
    if (yearLines.length > 1 && b.length === yearLines.length) b.forEach(l => raw.push([l]));
    else raw.push(b);
  }
  const out = [];
  for (const b of raw) {
    const joined = b.join(" ");
    const year = (joined.match(/\b(19|20)\d{2}\b/) || [])[0] || "";
    let degree = "", school = "";
    for (const l of b) for (const seg of l.split(/[,|•·]|\s[-–—]\s/).map(s => s.trim()).filter(Boolean)) {
      if (!degree && /(bachelor|master|b\.?\s?s\.?|m\.?\s?s\.?|b\.?\s?a\.?|m\.?\s?a\.?|b\.?eng|ph\.?\s?d|mba|associate|diploma|degree|certificate|major|h\.?n\.?d|gcse|a-?level|high school)/i.test(seg)) degree = stripTrail(seg.replace(/\b(19|20)\d{2}\b/g, ""));
      else if (!school && /(university|college|institute|school|academy|polytechnic|universidad|université)/i.test(seg)) school = stripTrail(seg.replace(/\b(19|20)\d{2}\b/g, ""));
    }
    if (!degree && !school) degree = stripTrail((b[0] || "").replace(/\b(19|20)\d{2}\b/g, ""));
    if (!degree && !school && !year) continue;
    out.push({ id: uid(), degree, field: "", school, year });
  }
  return out.slice(0, 8);
}

function parseSkills(lines) {
  const seen = new Set(), out = [];
  for (const tok of lines.join("\n").split(/[,;•▪◦‣·|\n\/]+/)) {
    let s = stripLead(tok).replace(/^[A-Za-z][A-Za-z &]+:\s*/, ""); // drop "Category:" labels
    s = s.replace(/^[-–—*\s]+/, "").trim();
    if (!s || s.length > 40 || /^(skills?|technologies|tools|expertise|proficienc)/i.test(s)) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue; seen.add(k);
    out.push({ name: s, level: "Intermediate" });
  }
  return out.slice(0, 30);
}

export function parseResumeText(raw) {
  const text = (raw || "").replace(/\r/g, "").replace(/ | | /g, " ").replace(/[ \t]+\n/g, "\n");
  const lines = text.split("\n").map(l => l.replace(/\s+$/, ""));
  const flat = text;

  const grab = (re) => { const x = flat.match(re); return x ? x[0].trim() : ""; };
  const clean = (u) => u.replace(/[.,;)]+$/, "");
  const email = grab(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  const phone = (flat.match(/\(?\+?\d[\d\s().\-]{6,}\d/) || [])[0]?.trim() || "";
  const linkedin = clean(grab(/(?:https?:\/\/)?(?:[\w.]*\.)?linkedin\.com\/[^\s)|,]+/i));
  const github = clean(grab(/(?:https?:\/\/)?(?:www\.)?github\.com\/[^\s)|,]+/i));
  const urls = flat.match(/(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+\.[a-z]{2,}(?:\/[^\s)|,]*)?/ig) || [];
  const emailLc = email.toLowerCase();
  const website = clean(urls.find(u => {
    const bare = u.toLowerCase().replace(/^https?:\/\//, "");
    return !/linkedin\.com|github\.com/i.test(u) && !u.includes("@") && !/\.(png|jpe?g|gif|pdf)$/i.test(u) && !(emailLc && emailLc.includes(bare));
  }) || "");

  // name + title from the first lines (above the first section header)
  const isContact = (l) => /@|https?:|linkedin|github|\b\d{3}\b/i.test(l);
  const topLines = [];
  for (const l of lines) { if (headerKey(l)) break; if (l.trim()) topLines.push(l.trim()); if (topLines.length >= 8) break; }
  let name = "", title = "";
  for (let i = 0; i < topLines.length; i++) {
    const l = topLines[i];
    if (isContact(l)) continue;
    if (!name && /^[A-Za-z][A-Za-z.'-]+(?:\s+[A-Za-z][A-Za-z.'-]+){1,3}$/.test(l) && l.length <= 42) {
      name = l;
      for (let j = i + 1; j < topLines.length; j++) {
        let t = topLines[j];
        if (isContact(t)) continue;
        t = stripTrail(t.replace(LOCATION, "").replace(/\s*[|·]\s*$/, "")); // drop a trailing location/separator
        if (t && t.length <= 70) { title = t; break; }
      }
      break;
    }
  }
  // personal location from the contact area
  let location = "";
  for (const l of topLines) { const m = l.match(LOCATION); if (m) { location = m[0]; break; } }

  // segment into sections by header lines
  const sec = {}; let cur = null;
  for (const line of lines) { const h = headerKey(line); if (h) { cur = h; sec[cur] = sec[cur] || []; } else if (cur) sec[cur].push(line); }

  const summary = (sec.summary || []).join(" ").replace(/\s+/g, " ").trim();
  const skills = sec.skills ? parseSkills(sec.skills) : [];
  const experience = parseExperience(sec.experience || []);
  const education = parseEducation(sec.education || []);

  const out = { personal: { name, title, email, phone, location, linkedin, github, website, summary, photo: "" } };
  if (skills.length) out.skills = skills;
  if (experience.length) out.experience = experience;
  if (education.length) out.education = education;

  out._found = [name, email, summary, skills.length, experience.length, education.length].filter(Boolean).length;
  return out;
}
